import { useState, useCallback, useEffect } from 'react';
import { MicrophoneDevice, MicrophonePermissionState } from '../types/microphone';

export interface UseMicrophoneDevicesReturn {
  state: MicrophonePermissionState;
  actions: {
    /** Request permission and enumerate devices */
    requestPermissionAndEnumerate: () => Promise<boolean>;
    /** Select a specific microphone device */
    selectDevice: (deviceId: string) => void;
    /** Refresh the list of available devices */
    refreshDevices: () => Promise<void>;
  };
}

/**
 * Hook for managing microphone device selection
 * Handles permission requests and device enumeration
 */
export const useMicrophoneDevices = (): UseMicrophoneDevicesReturn => {
  const [state, setState] = useState<MicrophonePermissionState>({
    hasPermission: false,
    isRequestingPermission: false,
    availableDevices: [],
    selectedDeviceId: null,
    isEnumeratingDevices: false,
  });

  // Enumerate available microphone devices
  const enumerateDevices = useCallback(async (): Promise<MicrophoneDevice[]> => {
    console.log('🎤 [useMicrophoneDevices] Enumerating audio input devices...');
    
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');
      
      console.log('🎤 [useMicrophoneDevices] Found audio input devices:', audioInputs);
      
      const microphoneDevices: MicrophoneDevice[] = audioInputs.map((device, index) => ({
        deviceId: device.deviceId,
        label: device.label || `Microphone ${index + 1}`,
        groupId: device.groupId,
        isDefault: device.deviceId === 'default' || index === 0,
      }));

      return microphoneDevices;
    } catch (error) {
      console.error('❌ [useMicrophoneDevices] Failed to enumerate devices:', error);
      return [];
    }
  }, []);

  // Request permission and enumerate devices
  const requestPermissionAndEnumerate = useCallback(async (): Promise<boolean> => {
    console.log('🔐 [useMicrophoneDevices] Requesting microphone permission and enumerating devices...');
    
    setState(prev => ({ 
      ...prev, 
      isRequestingPermission: true, 
      isEnumeratingDevices: true 
    }));

    try {
      // Request permission first
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      console.log('✅ [useMicrophoneDevices] Permission granted, stream:', stream);

      // Stop the stream immediately - we just needed permission
      stream.getTracks().forEach(track => {
        console.log('🛑 [useMicrophoneDevices] Stopping track:', track.label);
        track.stop();
      });

      // Now enumerate devices (labels will be available after permission)
      const devices = await enumerateDevices();
      
      // Select the first device by default
      const defaultDevice = devices.find(d => d.isDefault) || devices[0];
      
      setState(prev => ({
        ...prev,
        hasPermission: true,
        isRequestingPermission: false,
        isEnumeratingDevices: false,
        availableDevices: devices,
        selectedDeviceId: defaultDevice?.deviceId || null,
      }));

      console.log('✅ [useMicrophoneDevices] Devices enumerated:', devices);
      console.log('🎯 [useMicrophoneDevices] Default device selected:', defaultDevice);

      return true;
    } catch (error) {
      console.error('❌ [useMicrophoneDevices] Permission denied or enumeration failed:', error);
      
      setState(prev => ({
        ...prev,
        hasPermission: false,
        isRequestingPermission: false,
        isEnumeratingDevices: false,
        availableDevices: [],
        selectedDeviceId: null,
      }));

      return false;
    }
  }, [enumerateDevices]);

  // Select a specific device
  const selectDevice = useCallback((deviceId: string) => {
    console.log('🎯 [useMicrophoneDevices] Selecting device:', deviceId);
    
    setState(prev => ({
      ...prev,
      selectedDeviceId: deviceId,
    }));
  }, []);

  // Refresh devices (useful if devices are plugged/unplugged)
  const refreshDevices = useCallback(async () => {
    if (!state.hasPermission) {
      console.warn('⚠️ [useMicrophoneDevices] Cannot refresh devices without permission');
      return;
    }

    console.log('🔄 [useMicrophoneDevices] Refreshing device list...');
    
    setState(prev => ({ ...prev, isEnumeratingDevices: true }));

    try {
      const devices = await enumerateDevices();
      
      setState(prev => {
        // Keep the same selected device if it still exists
        const deviceStillExists = devices.some(d => d.deviceId === prev.selectedDeviceId);
        const newSelectedDevice = deviceStillExists 
          ? prev.selectedDeviceId 
          : devices.find(d => d.isDefault)?.deviceId || devices[0]?.deviceId || null;

        return {
          ...prev,
          isEnumeratingDevices: false,
          availableDevices: devices,
          selectedDeviceId: newSelectedDevice,
        };
      });

      console.log('✅ [useMicrophoneDevices] Device list refreshed:', devices);
    } catch (error) {
      console.error('❌ [useMicrophoneDevices] Failed to refresh devices:', error);
      setState(prev => ({ ...prev, isEnumeratingDevices: false }));
    }
  }, [state.hasPermission, enumerateDevices]);

  // Listen for device changes
  useEffect(() => {
    if (!state.hasPermission) return;

    const handleDeviceChange = () => {
      console.log('🔄 [useMicrophoneDevices] Device change detected, refreshing...');
      refreshDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [state.hasPermission, refreshDevices]);

  return {
    state,
    actions: {
      requestPermissionAndEnumerate,
      selectDevice,
      refreshDevices,
    },
  };
};